import { useCallback, useEffect, useState } from 'react';
import { crm, type TaskRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';

interface Props {
  /** Fehlt sie, wird eine neue Aufgabe angelegt. */
  task?: TaskRow;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Aufgabe anlegen oder bearbeiten.
 *
 * Deckt ab, was die Schnelleingabe auf der Liste nicht kann: Notiz,
 * Fälligkeit und die Verknüpfung mit einem Kontakt. Auftrag und Projekt
 * bleiben außen vor — die kommen aus deren eigenen Akten, wo der Bezug
 * ohnehin feststeht.
 */
export function TaskDialog({ task, onClose, onSaved }: Props) {
  const [titel, setTitel] = useState(task?.title ?? '');
  const [notiz, setNotiz] = useState(task?.notes ?? '');
  const [faellig, setFaellig] = useState(task?.dueAt?.slice(0, 10) ?? '');
  const [kontaktId, setKontaktId] = useState<string | null>(task?.contactId ?? null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const speichern = useCallback(async () => {
    const name = titel.trim();
    if (!name) {
      setFehler('Die Aufgabe braucht einen Titel.');
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      // Das Datumsfeld liefert nur den Tag. Als Ende des Tages gespeichert,
      // damit „heute fällig“ den ganzen Tag über gilt und nicht ab Mitternacht
      // überfällig ist.
      const dueAt = faellig ? new Date(`${faellig}T23:59:59`).toISOString() : null;
      const felder = {
        title: name,
        notes: notiz.trim() || null,
        dueAt,
        contactId: kontaktId,
      };
      if (task) await crm.tasks.update(task.id, felder);
      else await crm.tasks.create(felder);
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  }, [faellig, kontaktId, notiz, onSaved, task, titel]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">
          {task ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
        </h2>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Titel</span>
          <Input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            placeholder="Was ist zu tun?"
            autoFocus
          />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Notiz</span>
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Was man dazu wissen muss"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Fällig</span>
            <Input
              type="date"
              value={faellig}
              onChange={(e) => setFaellig(e.target.value)}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Kontakt</span>
            <EntityPicker
              kind="contact"
              value={kontaktId}
              onChange={setKontaktId}
              placeholder="— kein Kontakt —"
            />
          </label>
        </div>

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void speichern()} disabled={busy}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
